import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sun, CloudSun, Cloud, CloudRain, CloudSnow, Zap, CloudFog } from "lucide-react";

const oraIn = (fuso, d) => d.toLocaleTimeString("it-IT", { timeZone: fuso, hour: "2-digit", minute: "2-digit" });
const giornoIn = (fuso, d) => d.toLocaleDateString("it-IT", { timeZone: fuso, weekday: "long", day: "numeric", month: "long" });

const saluto = (d) => {
  const h = Number(d.toLocaleString("en-GB", { timeZone: "Atlantic/Canary", hour: "2-digit", hour12: false }).slice(0, 2));
  if (h >= 5 && h < 13) return "Buongiorno";
  if (h >= 13 && h < 18) return "Buon pomeriggio";
  if (h >= 18 && h < 23) return "Buona sera";
  return "Buona notte";
};

const IconaMeteo = ({ codice, condizione }) => {
  const t = (condizione || "").toLowerCase();
  const cls = "w-4 h-4";
  if (codice >= 95 || t.includes("tempora")) return <Zap className={`${cls} text-[#A8863F]`} />;
  if ((codice >= 71 && codice <= 86) || t.includes("neve")) return <CloudSnow className={`${cls} text-[#5A5A5A]`} />;
  if ((codice >= 51 && codice <= 82) || t.includes("piog") || t.includes("rovesc")) return <CloudRain className={`${cls} text-[#3b6ea5]`} />;
  if (codice === 45 || codice === 48 || t.includes("nebbia")) return <CloudFog className={`${cls} text-[#5A5A5A]`} />;
  if (codice === 3 || t.includes("coperto") || t.includes("nuvolos")) return <Cloud className={`${cls} text-[#5A5A5A]`} />;
  if (codice === 1 || codice === 2 || t.includes("poco")) return <CloudSun className={`${cls} text-[#A8863F]`} />;
  return <Sun className={`${cls} text-[#A8863F]`} />;
};

// Intestazione della Home: saluto sull'ora delle Canarie, data, orologi delle due sedi e meteo.
// Stessa struttura dell'intestazione di pratica e cliente: titolo, riga sotto, etichette, pulsanti.
export default function SalutoOrari({ children }) {
  const { user } = useAuth();
  const [ora, setOra] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setOra(new Date()), 1000 * 20);
    return () => clearInterval(t);
  }, []);

  const { data } = useQuery({
    queryKey: ["meteo"],
    queryFn: async () => (await api.get("/meteo")).data,
    refetchInterval: 1000 * 60 * 15,
    staleTime: 1000 * 60 * 10,
  });

  const nome = (user?.nome || "").split(" ")[0];
  const citta = data?.citta || [
    { id: "canarias", nome: "Canarias", fuso: "Atlantic/Canary" },
    { id: "italia", nome: "Verona", fuso: "Europe/Rome" },
  ];

  return (
    <header className="flex flex-col gap-3" data-testid="saluto-orari">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex flex-col gap-0.5">
          <h1 data-testid="saluto-utente"
            className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B] break-words">
            {saluto(ora)}{nome ? ` ${nome}` : ""}
          </h1>
          <p className="text-[14px] md:text-[16px] text-[#4A4A48] capitalize">{giornoIn("Atlantic/Canary", ora)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {citta.map((c) => (
            <span key={c.id} data-testid={`widget-${c.id}`}
              className="flex items-center gap-2 text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5">
              <span className="font-bold uppercase tracking-[0.6px] text-[11px] text-[#5A5A5A]">{c.nome}</span>
              <b data-testid={`ora-${c.id}`} className="text-[#1B2A4B] tabular-nums">{oraIn(c.fuso, ora)}</b>
              {c.temperatura !== undefined && (
                <span className="flex items-center gap-1">
                  <IconaMeteo codice={c.codice} condizione={c.condizione} />
                  <b data-testid={`meteo-${c.id}`} className="text-[#1B2A4B]">{c.temperatura}°</b>
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
      {children && <div className="flex flex-wrap gap-2.5">{children}</div>}
    </header>
  );
}
